import type { ChildColor } from "../_data/mock";

const colorMap: Record<
  ChildColor,
  { ring: string; track: string; text: string }
> = {
  orange: {
    ring: "stroke-[var(--child-1-primary)]",
    track: "stroke-[var(--child-1-track)]",
    text: "text-[var(--text-1)]",
  },
  blue: {
    ring: "stroke-[var(--child-2-primary)]",
    track: "stroke-[var(--child-2-track)]",
    text: "text-[var(--text-1)]",
  },
};

const pillMap: Record<ChildColor, string> = {
  orange: "bg-[var(--child-1-light)] text-[var(--child-1-primary)]",
  blue: "bg-[var(--child-2-light)] text-[var(--child-2-primary)]",
};

export function ProgressArc({
  progress,
  tone,
  label,
  pill,
  size = 96,
}: {
  progress: number;
  tone: ChildColor;
  label: string;
  pill: string;
  size?: number;
}) {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const startAngle = 180;
  const endAngle = 360;
  const arcLength = (Math.PI * radius * (endAngle - startAngle)) / 180;
  const dashOffset = arcLength * (1 - Math.max(0, Math.min(1, progress)));

  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const start = {
    x: cx + radius * Math.cos(toRad(startAngle)),
    y: cy + radius * Math.sin(toRad(startAngle)),
  };
  const end = {
    x: cx + radius * Math.cos(toRad(endAngle)),
    y: cy + radius * Math.sin(toRad(endAngle)),
  };
  const arcPath = `M ${start.x} ${start.y} A ${radius} ${radius} 0 0 1 ${end.x} ${end.y}`;
  const c = colorMap[tone];

  const arcHeight = Math.ceil(size / 2 + strokeWidth);

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: arcHeight }}>
        <svg width={size} height={arcHeight} className="block">
          <path
            d={arcPath}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            className={c.track}
          />
          <path
            d={arcPath}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={arcLength}
            strokeDashoffset={dashOffset}
            className={c.ring}
          />
        </svg>
        <div className="absolute inset-0 flex items-end justify-center pb-1">
          <span className={`text-[16px] font-semibold ${c.text}`}>{label}</span>
        </div>
      </div>
      <span
        className={`inline-flex h-6 min-w-14 items-center justify-center rounded-full px-2.5 text-[11.5px] font-semibold ${pillMap[tone]}`}
      >
        {pill}
      </span>
    </div>
  );
}
